/**
 * SAH End-of-Life blocker/event vocabulary mirror (P3-04).
 *
 * The database read-side mapper (`sah_eol_map_blocker_code`, migration
 * `20270027000000_sah_eol_worklist_monitoring_history.sql`) is the runtime
 * authority: it canonicalises output codes and fails loud on unregistered
 * values without ever rewriting stored events or evidence. This module is the
 * TypeScript mirror of that contract so web parsers, worklists and tests share
 * one registry. `packages/utils/src/__tests__/sah-eol-vocabulary.test.ts`
 * locks this list byte-equal to the migration's embedded list, so the two can
 * never silently diverge.
 */

export const SAH_EOL_CANONICAL_BLOCKER_CODES = [
  'ACTIVE_EOL_ENVELOPE_EXISTS',
  'BUDGETED_CARE_PLAN_NOT_REVIEWED',
  'CARE_PLAN_ALLOCATION_REVIEW',
  'CARE_PLAN_NOT_READY',
  'CARE_PLAN_SNAPSHOT_DRIFT',
  'CHANGE_SET_STATUS',
  'CURRENT_ACD_REQUIRED',
  'CURRENT_FUNDING_CHANGED',
  'CURRENT_PAYER_ACCOUNT_REQUIRED',
  'DELIVERY_FINANCE_RECONCILIATION',
  'EFFECTIVE_DATE_NOT_REACHED',
  'FEATURE_DISABLED',
  'FINANCE_RECONCILIATION_REQUIRED',
  'FIRST_SAH_PLAN_BASELINE_INCOMPLETE',
  'FUTURE_SERVICE_RECONCILIATION_REQUIRED',
  'INDEPENDENT_APPROVAL_REQUIRED',
  'OPEN_FUNDING_TRANSITION_BLOCKS_READY',
  'PALLIATIVE_PHASE_REVIEW_REQUIRED',
  'PALLIATIVE_PLAN_INCOMPLETE',
  'PAYER_ACCOUNT_PERIOD_COVERAGE_REQUIRED',
  'PAYER_ALLOCATION_INVALID',
  'PROGRAM_RATE_STALE',
  'QUALIFIED_CLINICAL_REVIEW_REQUIRED',
  'SAH_LIFECYCLE_REVIEW',
  'SAH_PATHWAY_CLOSEOUT_REQUIRED',
  'SAH_SHARE_EXCEEDS_EOL_ALLOCATION',
  'SEALED_ALLOCATION_DRIFT',
  'SERVICE_DISPOSITIONS_UNCONFIRMED',
  'SERVICE_PLAN_EMPTY',
  'SUCCESSOR_CARE_PLAN_REQUIRED',
  'TARGET_AN_ACC_CLASSIFICATION_REQUIRED',
  'TARGET_AUTHORISATION_REQUIRED',
  'TARGET_EVIDENCE_REQUIRED',
  'TARGET_FUNDING_ACCOUNT_REQUIRED',
  'TARGET_RESIDENTIAL_ADMISSION_REQUIRED',
  'WHOLE_LINE_PAYER_REQUIRED',
] as const;

export type SahEolBlockerCode = (typeof SAH_EOL_CANONICAL_BLOCKER_CODES)[number];

/**
 * Historical spellings mapped to their canonical form. Stored events and
 * evidence are never rewritten; this applies on read only, exactly like the
 * database mapper.
 */
export const SAH_EOL_LEGACY_BLOCKER_ALIASES: Readonly<Record<string, SahEolBlockerCode>> = {
  ACTIVE_EO_LIFE_ENVELOPE_EXISTS: 'ACTIVE_EOL_ENVELOPE_EXISTS',
};

export function isSahEolBlockerCode(value: string): value is SahEolBlockerCode {
  return (SAH_EOL_CANONICAL_BLOCKER_CODES as readonly string[]).includes(value);
}

/**
 * Strict mirror of `sah_eol_map_blocker_code`: canonicalises a code and
 * throws on anything outside the registry. Use in tests, tooling and
 * write-adjacent paths where an unknown code means a contract break.
 */
export function canonicaliseSahEolBlockerCode(code: string): SahEolBlockerCode {
  const mapped = SAH_EOL_LEGACY_BLOCKER_ALIASES[code] ?? code;
  if (!isSahEolBlockerCode(mapped)) {
    throw new Error(`sah_eol_blocker_vocabulary_unknown:${code}`);
  }
  return mapped;
}

/**
 * Lenient read-side variant for display surfaces: maps legacy aliases, passes
 * known codes through, and returns null for unknown values so a rendering
 * surface can fall back to the raw code without crashing. The database has
 * already canonicalised its output; this is defence in depth.
 */
export function canonicaliseSahEolBlockerCodeOrNull(
  code: string | null | undefined,
): SahEolBlockerCode | null {
  if (!code) return null;
  const mapped = SAH_EOL_LEGACY_BLOCKER_ALIASES[code] ?? code;
  return isSahEolBlockerCode(mapped) ? mapped : null;
}

/**
 * Next-action destination tokens emitted by the capability projection and the
 * provider worklist. The web layer maps tokens to routes; the database never
 * hardcodes web paths.
 */
export const SAH_EOL_DESTINATION_TOKENS = [
  'sah-eol-journey',
  'sah-eol-revision',
  'sah-eol-reschedule',
  'sah-eol-correction',
  'sah-eol-closeout',
  'sah-eol-reopen',
  'sah-eol-portal',
  'sah-budget-simulator',
  'palliative-panel',
] as const;

export type SahEolDestinationToken = (typeof SAH_EOL_DESTINATION_TOKENS)[number];

export function isSahEolDestinationToken(value: string): value is SahEolDestinationToken {
  return (SAH_EOL_DESTINATION_TOKENS as readonly string[]).includes(value);
}
